import React from "react";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { DecorationField, Balloon, Star } from "@/components/site/Decorations";
import { IMG } from "@/lib/siteData";

export default function PhilosophyPage() {
  return (
    <div data-testid="philosophy-page" className="overflow-hidden">
      <SEO
        title="Philosophy"
        description="Our philosophy: young children learn best through meaningful relationships, purposeful play, and inclusive, developmentally appropriate experiences."
      />
      <PageHero title="Philosophy" crumb="Philosophy" />

      <section className="relative py-20 md:py-28">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <img
                src={IMG.philosophyCollage[0]}
                alt="Children sitting together on a window seat"
                className="rounded-[2rem] object-cover w-full aspect-[3/4] shadow-lg col-span-1"
              />
              <div className="space-y-4">
                <img
                  src={IMG.philosophyCollage[1]}
                  alt="Toddler playing with kinetic sand and colourful shapes"
                  className="rounded-[2rem] object-cover w-full aspect-square shadow-lg"
                />
                <img
                  src={IMG.philosophyCollage[2]}
                  alt="Kindergarten friends smiling with arms around each other"
                  className="rounded-[2rem] object-cover w-full aspect-[4/3] shadow-lg"
                />
              </div>
              <div className="absolute -bottom-6 -left-4 animate-float">
                <Balloon color="#9333EA" size={50} />
              </div>
              <div className="absolute -top-4 right-2 animate-float-slow">
                <Star color="#FBBF24" size={40} />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-brand-green">
              Innovators Prep
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Philosophy <span className="text-brand-purple">Statement</span>
            </h2>
            <p className="mt-6 text-gray-600 text-lg leading-relaxed">
              At Innovators Prep, we believe every child is unique, capable, and deserving of a
              safe, nurturing, inclusive, and engaging learning environment. Our philosophy is
              grounded in the belief that young children learn best through meaningful
              relationships, purposeful play, exploration, creativity, and developmentally
              appropriate experiences that honor each child's individual strengths, needs,
              interests, and abilities.
            </p>
            <p className="mt-4 text-gray-600 text-lg leading-relaxed">
              We are committed to educating the whole child by supporting social-emotional,
              cognitive, physical, communication, and adaptive development. Through individualized
              instruction, hands-on learning, structured routines, and opportunities for choice and
              discovery, we encourage children to develop confidence, independence, curiosity, and
              a lifelong love of learning.
            </p>
            <p className="mt-4 text-gray-600 text-lg leading-relaxed">
              Inclusion and belonging are at the heart of our program. We value children of all
              abilities and developmental levels and believe every child should have meaningful
              opportunities to participate, build relationships, and experience success. Our
              educators and related service professionals work collaboratively to provide
              appropriate supports, accommodations, and individualized strategies so that each
              child can access learning and make meaningful progress.
            </p>
            <p className="mt-4 text-gray-600 text-lg leading-relaxed">
              We recognize families as children's first and most important teachers. Strong
              partnerships between families, educators, therapists, and the greater community are
              essential to children's success. We strive to maintain open communication, respect
              family perspectives, celebrate cultural and individual differences, and actively
              involve families in their child's educational experience.
            </p>
            <p className="mt-4 text-gray-600 text-lg leading-relaxed">
              At Innovators Prep, we are committed to creating an environment where children feel
              safe, valued, respected, supported, and inspired to grow. Through compassionate
              teaching, high-quality early childhood practices, collaboration, and individualized
              support, we prepare children with the foundational skills they need to become
              confident learners and successful members of their communities. Our philosophy is
              simple: <span className="font-semibold text-brand-purple-deep">every child belongs, every child can learn, and every child deserves the opportunity to reach their fullest potential.</span>
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
